import React, { useState, useEffect, useCallback } from 'react';
import {
  Search, Filter, ArrowUpRight, ArrowDownRight, ArrowLeftRight,
  Download, Trash2, RotateCcw, CheckCircle2, AlertCircle, FileText,
  Building2, Eye, Paperclip, ChevronLeft, ChevronRight, X, ExternalLink
} from 'lucide-react';
import api from '../../services/api';
import { formatCurrency, formatDate } from '../../utils/format';
import FinancialHeader from './FinancialHeader';
import NewTransactionModal from './NewTransactionModal';
import SettleModal from './SettleModal';
import DocumentViewerModal from '../../components/DocumentViewerModal';
import { openFileInNewTab, downloadFile } from '../../utils/fileViewer';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';

export default function TransactionsList() {
  const [ownerCompanyId, setOwnerCompanyId] = useState('all');
  const [ownerCompanies, setOwnerCompanies] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, total_pages: 1 });

  // Filters
  const [search, setSearch] = useState('');
  const [type, setType] = useState('all');
  const [classification, setClassification] = useState('all');
  const [status, setStatus] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Modals
  const [newTxModalOpen, setNewTxModalOpen] = useState(false);
  const [settleModalOpen, setSettleModalOpen] = useState(false);
  const [selectedTxForSettle, setSelectedTxForSettle] = useState(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedTxForDetails, setSelectedTxForDetails] = useState(null);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerFile, setViewerFile] = useState(null);

  // Load companies
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/owner-companies');
        setOwnerCompanies(res.data?.data || res.data || []);
      } catch (err) {
        console.error('Erro ao buscar empresas', err);
      }
    })();
  }, []);

  const loadTransactions = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = {
        page,
        limit: pagination.limit,
      };
      if (ownerCompanyId && ownerCompanyId !== 'all') params.owner_company_id = ownerCompanyId;
      if (type && type !== 'all') params.type = type;
      if (classification && classification !== 'all') params.classification = classification;
      if (status && status !== 'all') params.payment_status = status;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (search.trim()) params.search = search.trim();

      const res = await api.get('/financial/transactions', { params });
      const raw = res.data;
      const items = raw?.items || raw?.data || (Array.isArray(raw) ? raw : []);
      setTransactions(items);
      const pag = raw?.pagination || (raw?.total !== undefined ? {
        total: raw.total,
        page: raw.page || page,
        limit: raw.limit || pagination.limit,
        pages: raw.pages || 1
      } : null);
      if (pag) {
        setPagination(pag);
      }
    } catch (err) {
      console.error('Erro ao carregar transações', err);
      toast.error('Erro ao carregar extrato financeiro');
    } finally {
      setLoading(false);
    }
  }, [ownerCompanyId, type, classification, status, startDate, endDate, search, pagination.limit]);

  useEffect(() => {
    loadTransactions(1);
  }, [loadTransactions]);

  const handleSettle = (tx) => {
    setSelectedTxForSettle(tx);
    setSettleModalOpen(true);
  };

  const handleRevert = async (tx) => {
    if (!window.confirm(`Deseja realmente estornar a baixa do lançamento "${tx.description}"? O saldo bancário será ajustado.`)) {
      return;
    }
    try {
      await api.post(`/financial/transactions/${tx.id}/revert`);
      toast.success('Baixa estornada com sucesso');
      loadTransactions(pagination.page);
    } catch {
      toast.error('Erro ao estornar baixa');
    }
  };

  const handleDelete = async (tx) => {
    if (!window.confirm(`Deseja excluir o lançamento "${tx.description}"?`)) {
      return;
    }
    try {
      await api.delete(`/financial/transactions/${tx.id}`);
      toast.success('Lançamento excluído');
      loadTransactions(pagination.page);
    } catch {
      toast.error('Erro ao excluir lançamento');
    }
  };

  const handleExportCSV = () => {
    if (!transactions.length) {
      toast.error('Nenhum dado para exportar');
      return;
    }

    const headers = ['Data Vencimento', 'Data Pagamento', 'Tipo', 'Classificação', 'Descrição', 'Entidade', 'Empresa/CNPJ', 'Valor Previsto', 'Valor Pago', 'Status', 'Origem'];
    const rows = transactions.map(t => [
      t.due_date || '',
      t.payment_date || '',
      t.type === 'revenue' ? 'Receita' : t.type === 'expense' ? 'Despesa' : 'Transferência',
      t.classification || '',
      `"${(t.description || '').replace(/"/g, '""')}"`,
      `"${(t.client_name || t.supplier_name || t.vehicle_plate || '').replace(/"/g, '""')}"`,
      `"${(t.owner_company_name || '').replace(/"/g, '""')}"`,
      (t.amount || 0).toFixed(2),
      (t.paid_amount || 0).toFixed(2),
      t.payment_status || '',
      t.origin || 'manual'
    ]);

    const csvContent = 'data:text/csv;charset=utf-8,\uFEFF' + [headers.join(','), ...rows.map(e => e.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `lancamentos_financeiros_${new Date().toISOString().substring(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Relatório CSV exportado com sucesso');
  };

  // Summaries of current view
  const sumRevenue = transactions.filter(t => t.type === 'revenue').reduce((acc, t) => acc + (t.amount || 0), 0);
  const sumExpense = transactions.filter(t => t.type === 'expense').reduce((acc, t) => acc + (t.amount || 0), 0);
  const sumNet = sumRevenue - sumExpense;

  return (
    <div className="space-y-6">
      <FinancialHeader
        title="Extrato de Lançamentos"
        subtitle="Histórico e previsão de todas as receitas, despesas e transferências"
        ownerCompanyId={ownerCompanyId}
        setOwnerCompanyId={setOwnerCompanyId}
        ownerCompanies={ownerCompanies}
        onNewTransaction={() => setNewTxModalOpen(true)}
        onRefresh={() => loadTransactions(pagination.page)}
      />

      {/* Filter Toolbar */}
      <div className="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-xl p-4 space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          {/* Busca */}
          <div className="relative lg:col-span-2">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-neutral-400" />
            <Input
              type="text"
              placeholder="Buscar por descrição, cliente, fornecedor, placa..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 h-9 text-xs"
            />
          </div>

          {/* Tipo */}
          <Select value={type} onValueChange={setType}>
            <SelectTrigger className="h-9 text-xs">
              <SelectValue placeholder="Tipo" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os Tipos</SelectItem>
              <SelectItem value="revenue">Receitas (+)</SelectItem>
              <SelectItem value="expense">Despesas (-)</SelectItem>
              <SelectItem value="transfer">Transferências (⇄)</SelectItem>
            </SelectContent>
          </Select>

          {/* Status */}
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="h-9 text-xs">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os Status</SelectItem>
              <SelectItem value="pending">Pendente / Em Aberto</SelectItem>
              <SelectItem value="paid">Pago / Liquidado</SelectItem>
              <SelectItem value="overdue">Vencido / Em Atraso</SelectItem>
              <SelectItem value="canceled">Cancelado</SelectItem>
            </SelectContent>
          </Select>

          {/* Classificação */}
          <Select value={classification} onValueChange={setClassification}>
            <SelectTrigger className="h-9 text-xs">
              <SelectValue placeholder="Classificação" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas Classificações</SelectItem>
              <SelectItem value="operational">Operacional</SelectItem>
              <SelectItem value="fixed">Custo Fixo</SelectItem>
              <SelectItem value="variable">Custo Variável</SelectItem>
              <SelectItem value="extraordinary">Custo Extraordinário</SelectItem>
              <SelectItem value="investment">Investimento</SelectItem>
              <SelectItem value="refund">Reembolso</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Linha 2 de filtros: Datas e Ações */}
        <div className="flex flex-wrap items-center justify-between gap-3 pt-2 border-t border-neutral-100 dark:border-neutral-800">
          <div className="flex items-center gap-2 text-xs">
            <span className="text-neutral-500 font-medium">Vencimento:</span>
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="h-8 w-36 text-xs"
            />
            <span className="text-neutral-400">até</span>
            <Input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="h-8 w-36 text-xs"
            />
            {(startDate || endDate || search || type !== 'all' || status !== 'all' || classification !== 'all') && (
              <Button
                variant="ghost"
                size="xs"
                onClick={() => {
                  setSearch('');
                  setType('all');
                  setStatus('all');
                  setClassification('all');
                  setStartDate('');
                  setEndDate('');
                }}
                className="h-8 text-neutral-500 hover:text-neutral-800 gap-1 text-xs"
              >
                <X className="h-3.5 w-3.5" /> Limpar Filtros
              </Button>
            )}
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={handleExportCSV}
            className="h-8 text-xs gap-1.5"
          >
            <Download className="h-3.5 w-3.5" />
            Exportar CSV
          </Button>
        </div>
      </div>

      {/* Cards de Totais da Página Filtrada */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-900/40 rounded-xl p-3 flex items-center justify-between">
          <div>
            <p className="text-[11px] font-medium text-emerald-700 dark:text-emerald-400 uppercase tracking-wider">Receitas Filtradas</p>
            <p className="text-lg font-bold text-emerald-800 dark:text-emerald-300">{formatCurrency(sumRevenue)}</p>
          </div>
          <ArrowUpRight className="h-6 w-6 text-emerald-500 shrink-0" />
        </div>

        <div className="bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-900/40 rounded-xl p-3 flex items-center justify-between">
          <div>
            <p className="text-[11px] font-medium text-red-700 dark:text-red-400 uppercase tracking-wider">Despesas Filtradas</p>
            <p className="text-lg font-bold text-red-800 dark:text-red-300">{formatCurrency(sumExpense)}</p>
          </div>
          <ArrowDownRight className="h-6 w-6 text-red-500 shrink-0" />
        </div>

        <div className="bg-neutral-50 dark:bg-neutral-800/40 border border-neutral-200 dark:border-neutral-700 rounded-xl p-3 flex items-center justify-between">
          <div>
            <p className="text-[11px] font-medium text-neutral-600 dark:text-neutral-400 uppercase tracking-wider">Saldo do Período</p>
            <p className={`text-lg font-bold ${sumNet >= 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-700 dark:text-red-400'}`}>
              {formatCurrency(sumNet)}
            </p>
          </div>
          <Building2 className="h-6 w-6 text-neutral-400 shrink-0" />
        </div>
      </div>

      {/* Tabela de Transações */}
      <div className="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-xl overflow-hidden shadow-xs">
        <div className="overflow-x-auto">
          <table className="w-full text-xs text-left">
            <thead className="bg-neutral-50 dark:bg-neutral-800/50 text-neutral-500 font-semibold border-b border-neutral-200 dark:border-neutral-800">
              <tr>
                <th className="py-3 px-4">Vencimento</th>
                <th className="py-3 px-4">Tipo</th>
                <th className="py-3 px-4">Descrição</th>
                <th className="py-3 px-4">Entidade / Vínculo</th>
                <th className="py-3 px-4">Empresa (CNPJ)</th>
                <th className="py-3 px-4 text-right">Valor</th>
                <th className="py-3 px-4 text-center">Status</th>
                <th className="py-3 px-4 text-center">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100 dark:divide-neutral-800">
              {loading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <tr key={i}>
                    <td colSpan={8} className="py-4 px-4">
                      <div className="h-4 bg-neutral-100 dark:bg-neutral-800 rounded animate-pulse" />
                    </td>
                  </tr>
                ))
              ) : transactions.length === 0 ? (
                <tr>
                  <td colSpan={8} className="py-8 text-center text-neutral-400">
                    Nenhum lançamento financeiro encontrado com os filtros selecionados.
                  </td>
                </tr>
              ) : (
                transactions.map((tx) => {
                  const isRevenue = tx.type === 'revenue';
                  const isTransfer = tx.type === 'transfer';
                  const isOverdue = tx.payment_status === 'overdue';
                  const isPaid = tx.payment_status === 'paid';

                  return (
                    <tr key={tx.id} className="hover:bg-neutral-50 dark:hover:bg-neutral-800/40 transition-colors">
                      {/* Vencimento / Pagamento */}
                      <td className="py-3 px-4 whitespace-nowrap">
                        <div className="font-medium text-neutral-900 dark:text-neutral-100">
                          {formatDate(tx.due_date)}
                        </div>
                        {isPaid && tx.payment_date && (
                          <div className="text-[10px] text-emerald-600 dark:text-emerald-400">
                            Pago: {formatDate(tx.payment_date)}
                          </div>
                        )}
                      </td>

                      {/* Tipo */}
                      <td className="py-3 px-4 whitespace-nowrap">
                        {isRevenue ? (
                          <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400 font-semibold">
                            <ArrowUpRight className="h-3.5 w-3.5" /> Receita
                          </span>
                        ) : isTransfer ? (
                          <span className="inline-flex items-center gap-1 text-blue-600 dark:text-blue-400 font-semibold">
                            <ArrowLeftRight className="h-3.5 w-3.5" /> Transf.
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-red-600 dark:text-red-400 font-semibold">
                            <ArrowDownRight className="h-3.5 w-3.5" /> Despesa
                          </span>
                        )}
                      </td>

                      {/* Descrição & Classificação */}
                      <td className="py-3 px-4 max-w-xs">
                        <p className="font-medium text-neutral-900 dark:text-neutral-100 truncate" title={tx.description}>
                          {tx.description}
                        </p>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          {tx.classification === 'extraordinary' && (
                            <Badge variant="outline" className="text-[10px] bg-red-100 text-red-800 border-red-200 dark:bg-red-950/60 dark:text-red-300">
                              Extraordinário
                            </Badge>
                          )}
                          {tx.category_name && (
                            <span className="text-[10px] text-neutral-500 dark:text-neutral-400">
                              {tx.category_name}
                            </span>
                          )}
                          {tx.total_installments > 1 && (
                            <span className="text-[10px] font-semibold text-neutral-500">
                              ({tx.installment_number}/{tx.total_installments})
                            </span>
                          )}
                          {tx.attachments?.length > 0 && (
                            <Paperclip className="h-3 w-3 text-neutral-400" title={`${tx.attachments.length} anexo(s)`} />
                          )}
                        </div>
                      </td>

                      {/* Entidade / Vínculo */}
                      <td className="py-3 px-4 whitespace-nowrap text-neutral-600 dark:text-neutral-300">
                        {tx.client_name ? (
                          <span title={`Cliente: ${tx.client_name}`}>🏢 {tx.client_name}</span>
                        ) : tx.supplier_name ? (
                          <span title={`Fornecedor: ${tx.supplier_name}`}>📦 {tx.supplier_name}</span>
                        ) : tx.vehicle_plate ? (
                          <span title={`Veículo: ${tx.vehicle_plate}`}>🚌 {tx.vehicle_plate}</span>
                        ) : (
                          <span className="text-neutral-400">-</span>
                        )}
                      </td>

                      {/* Empresa (CNPJ) */}
                      <td className="py-3 px-4 whitespace-nowrap text-neutral-600 dark:text-neutral-300">
                        <span className="font-medium">{tx.owner_company_name || 'Dany Trans'}</span>
                      </td>

                      {/* Valor */}
                      <td className="py-3 px-4 whitespace-nowrap text-right">
                        <div className={`font-bold ${isRevenue ? 'text-emerald-600 dark:text-emerald-400' : isTransfer ? 'text-blue-600 dark:text-blue-400' : 'text-neutral-900 dark:text-neutral-100'}`}>
                          {isRevenue ? '+' : isTransfer ? '' : '-'} {formatCurrency(isPaid && tx.net_amount ? tx.net_amount : tx.amount)}
                        </div>
                        {isPaid && (tx.interest_amount > 0 || tx.discount_amount > 0) && (
                          <div className="text-[10px] text-neutral-400">
                            Bruto: {formatCurrency(tx.amount)}
                          </div>
                        )}
                      </td>

                      {/* Status */}
                      <td className="py-3 px-4 whitespace-nowrap text-center">
                        {isPaid ? (
                          <Badge className="bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300 border-emerald-200">
                            Pago
                          </Badge>
                        ) : isOverdue ? (
                          <Badge className="bg-red-100 text-red-800 dark:bg-red-950/60 dark:text-red-300 border-red-200">
                            Vencido
                          </Badge>
                        ) : tx.payment_status === 'canceled' ? (
                          <Badge variant="outline" className="text-neutral-400">
                            Cancelado
                          </Badge>
                        ) : (
                          <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-300 border-amber-200">
                            Pendente
                          </Badge>
                        )}
                      </td>

                      {/* Ações */}
                      <td className="py-3 px-4 whitespace-nowrap text-center">
                        <div className="flex items-center justify-center gap-1.5">
                          {/* Botão Baixar */}
                          {!isPaid && tx.payment_status !== 'canceled' && (
                            <Button
                              size="xs"
                              onClick={() => handleSettle(tx)}
                              className={`h-7 px-2 text-[11px] font-semibold text-white ${isRevenue ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-brand hover:bg-[#A81E23]'}`}
                              title={isRevenue ? 'Confirmar Recebimento' : 'Dar Baixa / Pagar'}
                            >
                              Baixar
                            </Button>
                          )}

                          {/* Estornar */}
                          {isPaid && (
                            <Button
                              size="xs"
                              variant="ghost"
                              onClick={() => handleRevert(tx)}
                              className="h-7 w-7 p-0 text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                              title="Estornar baixa"
                            >
                              <RotateCcw className="h-3.5 w-3.5" />
                            </Button>
                          )}

                          {/* Ver Detalhes */}
                          <Button
                            size="xs"
                            variant="ghost"
                            onClick={() => {
                              setSelectedTxForDetails(tx);
                              setDetailsModalOpen(true);
                            }}
                            className="h-7 w-7 p-0 text-neutral-500 hover:text-neutral-800"
                            title="Ver detalhes completos"
                          >
                            <Eye className="h-3.5 w-3.5" />
                          </Button>

                          {/* Excluir (se manual) */}
                          {(!tx.origin || tx.origin === 'manual') && !isPaid && (
                            <Button
                              size="xs"
                              variant="ghost"
                              onClick={() => handleDelete(tx)}
                              className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                              title="Excluir lançamento"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Paginação */}
        {pagination.total_pages > 1 && (
          <div className="p-3 border-t border-neutral-100 dark:border-neutral-800 flex items-center justify-between text-xs text-neutral-500">
            <span>
              Mostrando {transactions.length} de {pagination.total} lançamentos
            </span>
            <div className="flex items-center gap-1.5">
              <Button
                variant="outline"
                size="xs"
                disabled={pagination.page <= 1}
                onClick={() => loadTransactions(pagination.page - 1)}
                className="h-7 text-xs"
              >
                <ChevronLeft className="h-3.5 w-3.5" /> Anterior
              </Button>
              <span className="px-2 font-medium">
                Página {pagination.page} de {pagination.total_pages}
              </span>
              <Button
                variant="outline"
                size="xs"
                disabled={pagination.page >= pagination.total_pages}
                onClick={() => loadTransactions(pagination.page + 1)}
                className="h-7 text-xs"
              >
                Próxima <ChevronRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Modal de Detalhes do Lançamento */}
      {selectedTxForDetails && (
        <Dialog open={detailsModalOpen} onOpenChange={setDetailsModalOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-base font-bold">Detalhes do Lançamento Financeiro</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 text-xs text-neutral-700 dark:text-neutral-300">
              <div className="grid grid-cols-2 gap-2 p-3 bg-neutral-50 dark:bg-neutral-800/50 rounded-lg">
                <div>
                  <span className="text-neutral-400">Tipo:</span>
                  <p className="font-semibold capitalize">{selectedTxForDetails.type === 'revenue' ? 'Receita' : selectedTxForDetails.type === 'expense' ? 'Despesa' : 'Transferência'}</p>
                </div>
                <div>
                  <span className="text-neutral-400">Classificação:</span>
                  <p className="font-semibold capitalize">{selectedTxForDetails.classification || 'Operacional'}</p>
                </div>
                <div>
                  <span className="text-neutral-400">Data de Vencimento:</span>
                  <p className="font-semibold">{formatDate(selectedTxForDetails.due_date)}</p>
                </div>
                <div>
                  <span className="text-neutral-400">Data de Pagamento:</span>
                  <p className="font-semibold">{formatDate(selectedTxForDetails.payment_date) || '-'}</p>
                </div>
                <div>
                  <span className="text-neutral-400">Valor Bruto:</span>
                  <p className="font-bold text-sm">{formatCurrency(selectedTxForDetails.amount)}</p>
                </div>
                <div>
                  <span className="text-neutral-400">Valor Líquido Baixado:</span>
                  <p className="font-bold text-sm text-emerald-600">{formatCurrency(selectedTxForDetails.net_amount || selectedTxForDetails.amount)}</p>
                </div>
              </div>

              <div>
                <span className="text-neutral-400">Descrição:</span>
                <p className="font-medium">{selectedTxForDetails.description}</p>
              </div>

              {selectedTxForDetails.owner_company_name && (
                <div>
                  <span className="text-neutral-400">Empresa Responsável:</span>
                  <p className="font-medium">{selectedTxForDetails.owner_company_name} {selectedTxForDetails.owner_company_cnpj ? `(${selectedTxForDetails.owner_company_cnpj})` : ''}</p>
                </div>
              )}

              {selectedTxForDetails.bank_account_name && (
                <div>
                  <span className="text-neutral-400">Conta Bancária / Caixa:</span>
                  <p className="font-medium">{selectedTxForDetails.bank_account_name}</p>
                </div>
              )}

              {selectedTxForDetails.notes && (
                <div>
                  <span className="text-neutral-400">Observações:</span>
                  <p className="bg-neutral-50 dark:bg-neutral-800 p-2 rounded">{selectedTxForDetails.notes}</p>
                </div>
              )}

              {selectedTxForDetails.attachments?.length > 0 && (
                <div>
                  <span className="text-neutral-400 mb-1 block">Anexos / Comprovantes ({selectedTxForDetails.attachments.length}):</span>
                  <div className="space-y-1.5">
                    {selectedTxForDetails.attachments.map((att, idx) => {
                      const url = att.file_url || att.url;
                      const name = att.filename || att.name || 'Comprovante';
                      return (
                        <div
                          key={idx}
                          className="flex items-center justify-between gap-2 p-2 rounded bg-neutral-100 dark:bg-neutral-800 text-xs font-medium"
                        >
                          <div className="flex items-center gap-2 truncate flex-1 min-w-0">
                            <FileText className="h-4 w-4 text-brand shrink-0" />
                            <button
                              type="button"
                              onClick={() => {
                                setViewerFile({
                                  url,
                                  name,
                                  title: `Comprovante - ${selectedTxForDetails.description || 'Lançamento'}`
                                });
                                setViewerOpen(true);
                              }}
                              className="text-brand hover:underline truncate text-left"
                              title="Clique para visualizar"
                            >
                              {name}
                            </button>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <button
                              type="button"
                              onClick={() => {
                                setViewerFile({
                                  url,
                                  name,
                                  title: `Comprovante - ${selectedTxForDetails.description || 'Lançamento'}`
                                });
                                setViewerOpen(true);
                              }}
                              className="p-1 rounded hover:bg-neutral-200 dark:hover:bg-neutral-700 text-neutral-600 hover:text-brand"
                              title="Visualizar documento"
                            >
                              <Eye className="h-3.5 w-3.5" />
                            </button>
                            <button
                              type="button"
                              onClick={() => openFileInNewTab(url, name)}
                              className="p-1 rounded hover:bg-neutral-200 dark:hover:bg-neutral-700 text-neutral-600 hover:text-brand"
                              title="Abrir em nova guia"
                            >
                              <ExternalLink className="h-3.5 w-3.5" />
                            </button>
                            <button
                              type="button"
                              onClick={() => downloadFile(url, name)}
                              className="p-1 rounded hover:bg-neutral-200 dark:hover:bg-neutral-700 text-neutral-600 hover:text-brand"
                              title="Baixar arquivo"
                            >
                              <Download className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" size="sm" onClick={() => setDetailsModalOpen(false)}>
                Fechar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Modals */}
      <NewTransactionModal
        open={newTxModalOpen}
        onOpenChange={setNewTxModalOpen}
        onSuccess={() => loadTransactions(pagination.page)}
      />

      <SettleModal
        open={settleModalOpen}
        onOpenChange={setSettleModalOpen}
        transaction={selectedTxForSettle}
        onSuccess={() => loadTransactions(pagination.page)}
      />

      <DocumentViewerModal
        isOpen={viewerOpen}
        onClose={() => setViewerOpen(false)}
        fileUrl={viewerFile?.url}
        fileName={viewerFile?.name}
        title={viewerFile?.title}
      />
    </div>
  );
}
